/**
 * Query Key Factories for TanStack Query
 *
 * Based on TkDodo's effective React Query keys pattern:
 * https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 *
 * These factories provide a standardized way to create query keys throughout the application.
 * Benefits:
 * - Type safety for query keys
 * - Consistent patterns across the app
 * - Easy invalidation of related queries
 * - Better organization and maintainability
 */

export const projectQueryKeys = {
  all: ["projects"] as const,
  lists: () => [...projectQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...projectQueryKeys.lists(), { filters }] as const,
  details: () => [...projectQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...projectQueryKeys.details(), id] as const,
  filterCountries: () => [...projectQueryKeys.all, "filter-countries"] as const,
  approvedDuplicates: () => [...projectQueryKeys.all, "approved-duplicates"] as const,
}

export const auditLogQueryKeys = {
  all: ["audit-logs"] as const,
  lists: () => [...auditLogQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...auditLogQueryKeys.lists(), { filters }] as const,
}

export const fixtureValidationQueryKeys = {
  all: ["fixture-validation"] as const,
  lists: () => [...fixtureValidationQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...fixtureValidationQueryKeys.lists(), { filters }] as const,
}

export const merchandiseGroupQueryKeys = {
  all: ["merchandise-groups"] as const,
  lists: () => [...merchandiseGroupQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...merchandiseGroupQueryKeys.lists(), { filters }] as const,
  details: () => [...merchandiseGroupQueryKeys.all, "detail"] as const,
  detail: (groupId: string) => [...merchandiseGroupQueryKeys.details(), groupId] as const,
}

export const explanationQueryKeys = {
  all: ["explanations"] as const,
  byProject: (projectId: string) => [...explanationQueryKeys.all, "project", projectId] as const,
  assignments: (projectId: string, includeExplanations: boolean) =>
    [...explanationQueryKeys.byProject(projectId), "assignments", { includeExplanations }] as const,
}

export const storesQueryKeys = {
  all: ["stores"] as const,
  lists: () => [...storesQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...storesQueryKeys.lists(), { filters }] as const,
  details: () => [...storesQueryKeys.all, "detail"] as const,
  detail: (storeId: string) => [...storesQueryKeys.details(), storeId] as const,
}

export const settingsQueryKeys = {
  all: ["settings"] as const,
  overview: () => [...settingsQueryKeys.all, "overview"] as const,
}

export const filesQueryKeys = {
  all: ["files"] as const,
  lists: () => [...filesQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...filesQueryKeys.lists(), { filters }] as const,
  details: () => [...filesQueryKeys.all, "detail"] as const,
  detail: (fileId: string) => [...filesQueryKeys.details(), fileId] as const,
}

export const fileSharesQueryKeys = {
  all: ["file-shares"] as const,
  lists: () => [...fileSharesQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...fileSharesQueryKeys.lists(), { filters }] as const,
}

export const storeConfigurationQueryKeys = {
  all: ["store-configurations"] as const,
  lists: () => [...storeConfigurationQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...storeConfigurationQueryKeys.lists(), { filters }] as const,
  details: () => [...storeConfigurationQueryKeys.all, "detail"] as const,
  detail: (versionId: string) => [...storeConfigurationQueryKeys.details(), versionId] as const,
  active: () => [...storeConfigurationQueryKeys.all, "active"] as const,
  activeWithLocale: (locale?: string) =>
    [...storeConfigurationQueryKeys.active(), { locale }] as const,
  json: (versionId: string, locale?: string) =>
    [...storeConfigurationQueryKeys.detail(versionId), "json", { locale }] as const,
  version: (versionId: string) =>
    [...storeConfigurationQueryKeys.detail(versionId), "version"] as const,
  translations: (versionId: string) =>
    [...storeConfigurationQueryKeys.detail(versionId), "translations"] as const,
  fixtureTypes: () => [...storeConfigurationQueryKeys.all, "fixture-types"] as const,
  planogramTemplates: (versionId: string) =>
    [...storeConfigurationQueryKeys.detail(versionId), "planogram-templates"] as const,
  splitGroups: (versionId: string) =>
    [...storeConfigurationQueryKeys.detail(versionId), "split-groups"] as const,
  aisleRules: (versionId: string) =>
    [...storeConfigurationQueryKeys.detail(versionId), "aisle-rules"] as const,
}

export const countryCodeMappingQueryKeys = {
  all: ["country-code-mappings"] as const,
  lists: () => [...countryCodeMappingQueryKeys.all, "list"] as const,
  list: () => [...countryCodeMappingQueryKeys.lists()] as const,
}

export const lomQueryKeys = {
  all: ["list-of-materials"] as const,
  versions: (requested?: string) => [...lomQueryKeys.all, "versions", { requested }] as const,
  overview: (configVersionId: string) =>
    [...lomQueryKeys.all, "overview", configVersionId] as const,
  recentChanges: (configVersionId: string, entityId?: string) =>
    [...lomQueryKeys.all, "recent-changes", configVersionId, { entityId }] as const,
  coding: (configVersionId: string) => [...lomQueryKeys.all, "coding", configVersionId] as const,
  stores: (query: string) => [...lomQueryKeys.all, "stores", query] as const,
  preview: (configVersionId: string, storeId: string) =>
    [...lomQueryKeys.all, "preview", configVersionId, storeId] as const,
  codingGaps: (configVersionId: string) =>
    [...lomQueryKeys.all, "coding-gaps", configVersionId] as const,
  fillInImpact: (configVersionId: string) =>
    [...lomQueryKeys.all, "fill-in-impact", configVersionId] as const,
}

export const bomQueryKeys = {
  all: ["bom"] as const,
  shelfCodes: (configVersionId: string) =>
    [...bomQueryKeys.all, "shelf-codes", configVersionId] as const,
  shelfCode: (id: string) => [...bomQueryKeys.all, "shelf-code", id] as const,
  computation: (projectId: string) => [...bomQueryKeys.all, "computation", projectId] as const,
  fixtureTypeArticles: (configVersionId: string) =>
    [...bomQueryKeys.all, "fixture-type-articles", configVersionId] as const,
  fixtureTypeShelfCodes: (configVersionId: string) =>
    [...bomQueryKeys.all, "fixture-type-shelf-codes", configVersionId] as const,
  shelf125Articles: (configVersionId: string) =>
    [...bomQueryKeys.all, "shelf-125-articles", configVersionId] as const,
  lomExtras: (configVersionId: string) =>
    [...bomQueryKeys.all, "lom-extras", configVersionId] as const,
  lomExtraQuantityRules: (configVersionId: string) =>
    [...bomQueryKeys.all, "lom-extra-quantity-rules", configVersionId] as const,
  materialRules: (configVersionId: string) =>
    [...bomQueryKeys.all, "material-rules", configVersionId] as const,
  ruleArticles: (configVersionId: string) =>
    [...bomQueryKeys.all, "rule-articles", configVersionId] as const,
  articleCatalogue: (configVersionId: string) =>
    [...bomQueryKeys.all, "article-catalogue", configVersionId] as const,
}

export const layoutExplorerQueryKeys = {
  all: ["layout-explorer"] as const,
  bandwidths: () => [...layoutExplorerQueryKeys.all, "bandwidths"] as const,
  segmentationOptions: () => [...layoutExplorerQueryKeys.all, "segmentation-options"] as const,
  results: (conditions: unknown) =>
    [...layoutExplorerQueryKeys.all, "results", conditions] as const,
}

export const storeSizeBucketsQueryKeys = {
  all: ["store-size-buckets"] as const,
  list: () => [...storeSizeBucketsQueryKeys.all, "list"] as const,
}

export const standardStoreQueryKeys = {
  all: ["standard-store"] as const,
  baseline: () => [...standardStoreQueryKeys.all, "baseline"] as const,
}

export const shelfTypeOrderQueryKeys = {
  all: ["shelf-type-order"] as const,
  list: () => [...shelfTypeOrderQueryKeys.all, "list"] as const,
}

export const reportsQueryKeys = {
  all: ["reports"] as const,
  filterCountries: () => [...reportsQueryKeys.all, "filter-countries"] as const,
  metersOverview: (filters: unknown) =>
    [...reportsQueryKeys.all, "meters-overview", filters] as const,
  storesBySize: (filters: unknown) => [...reportsQueryKeys.all, "stores-by-size", filters] as const,
  storesBySizeFilterCountries: () =>
    [...reportsQueryKeys.all, "stores-by-size", "filter-countries"] as const,
  shallowShelves: (filters: unknown) =>
    [...reportsQueryKeys.all, "shallow-shelves", filters] as const,
  presentationLocations: (filters: unknown) =>
    [...reportsQueryKeys.all, "presentation-locations", filters] as const,
  buyingComparison: (filters: unknown) =>
    [...reportsQueryKeys.all, "buying-comparison", filters] as const,
  storeInfo: (filters: unknown) => [...reportsQueryKeys.all, "store-info", filters] as const,
  materialsOverview: (filters: unknown) =>
    [...reportsQueryKeys.all, "materials-overview", filters] as const,
}

export const evaluationQueryKeys = {
  all: ["evaluation"] as const,
  benchmarks: () => [...evaluationQueryKeys.all, "benchmarks"] as const,
  runs: (filters?: Record<string, unknown>) =>
    [...evaluationQueryKeys.all, "runs", filters] as const,
  run: (runId: string) => [...evaluationQueryKeys.all, "run", runId] as const,
  batches: (filters?: Record<string, unknown>) =>
    [...evaluationQueryKeys.all, "batches", filters] as const,
  batch: (batchId: string) => [...evaluationQueryKeys.all, "batch", batchId] as const,
}

export const flagQueryKeys = {
  all: ["flags"] as const,
  lists: () => [...flagQueryKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...flagQueryKeys.lists(), { filters }] as const,
  details: () => [...flagQueryKeys.all, "detail"] as const,
  detail: (flagId: string) => [...flagQueryKeys.details(), flagId] as const,
  evaluate: (flagKey: string, context: Record<string, unknown>) =>
    [...flagQueryKeys.all, "evaluate", flagKey, { context }] as const,
}
